import { supabase } from '../supabase';

export async function addToProtectionList(
  playerId: number,
  userId: number,
  leagueId: number
) {
  try {
    const { data, error } = await supabase
      .from('protection_list')
      .insert({
        playerid: playerId,
        user_id: userId,
        league_id: leagueId
      })
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error adding to protection list:', error);
    return { data: null, error };
  }
}

export async function removeFromProtectionList(
  playerId: number,
  userId: number,
  leagueId: number
) {
  try {
    const { data, error } = await supabase
      .from('protection_list')
      .delete()
      .eq('playerid', playerId)
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (error) throw error;
    return { data, error: null };
  } catch (error: any) {
    console.error('Error removing from protection list:', error);
    return { data: null, error };
  }
}

export async function getProtectionList(
  userId: number,
  leagueId: number
) {
  try {
    const { data, error } = await supabase
      .from('protection_list')
      .select('playerid')
      .eq('user_id', userId)
      .eq('league_id', leagueId);

    if (error) throw error;
    return { data: data?.map(item => item.playerid) || [], error: null };
  } catch (error: any) {
    console.error('Error getting protection list:', error);
    return { data: [], error };
  }
}

export async function getProtectionLimits(leagueId: number) {
  try {
    const { data, error } = await supabase
      .from('league_settings')
      .select('number_foward_protect, number_def_protect, number_goalie_protect, number_reserve_protect')
      .eq('league_id', leagueId)
      .single();

    if (error) throw error;
    return { 
      data: {
        forwards: data?.number_foward_protect || 8,
        defensemen: data?.number_def_protect || 3,
        goalies: data?.number_goalie_protect || 1,
        reserves: data?.number_reserve_protect || 0 // Added reserves
      }, 
      error: null 
    };
  } catch (error: any) {
    console.error('Error getting protection limits:', error);
    return { 
      data: { forwards: 8, defensemen: 3, goalies: 1, reserves: 0 }, 
      error 
    };
  }
}

export async function validateProtectionLimit(
  playerId: number,
  userId: number,
  leagueId: number,
  playerPosition: string,
  isGoalie: boolean = false,
  isReserve: boolean = false // Added isReserve parameter
) {
  try {
    // Get protection limits from league settings
    const { data: limits, error: limitsError } = await getProtectionLimits(leagueId);
    if (limitsError) throw limitsError;

    // Get current protection list
    const { data: protectedPlayerIds, error: protectedError } = await getProtectionList(userId, leagueId);
    if (protectedError) throw protectedError;

    if (protectedPlayerIds.length === 0) {
      return { canAdd: true, error: null };
    }

    // Get player details for protected players
    const { data: myPlayers, error: playersError } = await supabase
      .from('getmyteamplayer')
      .select('playerid, positioncode,isnhl')
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .in('playerid', protectedPlayerIds);

    const { data: myGoalies, error: goaliesError } = await supabase
      .from('getmyteamgoalie')
      .select('playerid,isnhl')
      .eq('user_id', userId)
      .eq('league_id', leagueId)
      .in('playerid', protectedPlayerIds);

    if (playersError) throw playersError;
    if (goaliesError) throw goaliesError;

    // Count current protected players by position
    let currentForwards = 0;
    let currentDefensemen = 0;
    let currentGoalies = myGoalies?.filter(g => g.isnhl).length || 0; // Exclude reserve goalies

    myPlayers?.forEach((player: any) => {
      if (player.positioncode === 'D' && player.isnhl) {
        currentDefensemen++;
      } else if (['C', 'L', 'R'].includes(player.positioncode) && player.isnhl) {
        currentForwards++;
      }
    });

    // Skip position limit checks for reserve players
    if (isReserve) {
      return { canAdd: true, error: null };
    }

    // Check if adding this player would exceed limits
    if (isGoalie) {
      if (currentGoalies >= limits.goalies) {
        return {
          canAdd: false,
          error: `Limite de gardiens protégés atteinte (${currentGoalies}/${limits.goalies})`
        };
      }
    } else if (playerPosition === 'D') {
      if (currentDefensemen >= limits.defensemen) {
        return {
          canAdd: false,
          error: `Limite de défenseurs protégés atteinte (${currentDefensemen}/${limits.defensemen})`
        };
      }
    } else if (['C', 'L', 'R'].includes(playerPosition)) {
      if (currentForwards >= limits.forwards) {
        return {
          canAdd: false,
          error: `Limite d'attaquants protégés atteinte (${currentForwards}/${limits.forwards})`
        };
      }
    }

    return { canAdd: true, error: null };
  } catch (error: any) {
    console.error('Error validating protection limit:', error);
    return { canAdd: false, error: 'Erreur lors de la validation' };
  }
}